<template>
  <div ref="formRootEl" data-testid="plp-products-list-skeleton">
    <form type="form">
      <ProductListLayout
        :show-filter="showProductListMetaFunctions"
        :results-text="''"
        :is-full-size="isFullSize"
        :data-testid-productlist-total="21"
      >
        <template v-if="showProductListMetaFunctions" #filter>
          <ProductFilterSkeleton />
        </template>
        <template #actions>
          <div class="actions__container"></div>
        </template>
        <template v-if="showProductListMetaFunctions" #filterTags>
          <TagList :tags="[]" :is-small="true" />
        </template>
        <template #list>
          <ProductList
            :items="loadingList"
            :size="productListSize"
            :display-mode="displayMode"
            :default-columns="defaultColumns"
            :attribute-columns="productsList.attributeColumns"
          >
            <template #card-footer>
              <LoadingSkeleton type="button" />
            </template>
          </ProductList>
          <ProductListPagination class="product-list-pagination" :items="[]" />
        </template>
      </ProductListLayout>
    </form>
  </div>
</template>

<script setup lang="ts">
import type {
  DefaultColumn,
  ProductCardProps,
  AttributeColumn
} from '@hypercodestudio/basler-components';
import {
  LoadingSkeleton,
  ProductList,
  ProductListLayout,
  ProductListPagination,
  TagList
} from '@hypercodestudio/basler-components';

import ProductFilterSkeleton from '~/components/shop/skeletons/ProductFilterSkeleton.vue';
import { resolveProductListView } from '~/stores/productFilterStore/helper/resolveProductListView';
import { createProductListLoadingItems } from '~/utils/shop/createProductListLoadingItems';

interface Props {
  type: 'Specifications' | 'Discontinued' | 'Plain';
  view: 'grid' | 'table-list';
  isFullSize?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  isFullSize: true
});

const { $textDictionary } = useNuxtApp();

const displayMode = computed(() => resolveProductListView(props.view));

const productListSize = computed(() => {
  return props.type === 'Plain' ? 'full' : 'three-quarters';
});

const productListItemsSize = computed(() => {
  return props.type === 'Plain' ? 8 : 21;
});

const showProductListMetaFunctions = computed(() => {
  return props.type !== 'Plain';
});

const loadingList = computed(() =>
  createProductListLoadingItems(productListItemsSize.value)
);

const productsList = computed<{
  list: ProductCardProps[];
  attributeColumns: AttributeColumn[];
}>(() => ({
  list: [],
  attributeColumns: []
}));

const defaultColumns = computed<DefaultColumn[]>(() => {
  const columns: DefaultColumn[] = [
    {
      key: 'media',
      columnLabel:
        $textDictionary.value['productsList.tableList.column.media'] ?? '',
      hideColumnLabel: true
    },
    {
      key: 'title',
      columnLabel:
        $textDictionary.value['productsList.tableList.column.title'] ?? ''
    }
  ];

  if (props.type === 'Specifications') {
    columns.push({
      key: 'priceText',
      columnLabel:
        $textDictionary.value['productsList.tableList.column.priceText'] ?? ''
    });
  }

  const hasDescription = productsList.value.list.some(
    (card: ProductCardProps) => card.product.description === ''
  );
  if (!hasDescription) {
    columns.push({
      key: 'description',
      columnLabel:
        $textDictionary.value['productsList.tableList.column.description'] ??
        '',
      hideColumnLabel: true
    });
  }

  return columns;
});
</script>
